// ==================== 商城端 WebSocket 实时通信封装 ====================
// 说明：
//   - 聊天消息改为通过 WebSocket 实时收发，HTTP 接口仅在 WebSocket 不可用时兜底
//     （历史消息、会话列表、图片上传仍走 HTTP，消息落库两边共用同一张表）。
//   - 连接地址：ws(s)://<当前域名>/ws
//     · 本地开发：由 vue.config.js 的 devServer.proxy 转发到 ws://127.0.0.1:9501
//     · 生产环境：由 Nginx 将 /ws 反向代理到 WebSocket 服务（见 server/WEBSOCKET_README.md）
//   - 断线自动重连（指数退避，最长 10 秒），重连后自动重新鉴权。
//
// 事件（on/off）：
//   'ready'   服务端鉴权成功 { role, id, sessionId }
//   'message' 收到新消息    { sessionId, message, session }
//   'session' 会话变化      { session }
//   'read'    已读回执变化  { sessionId, agent_last_read_id?, user_last_read_id? }
//   'open'    连接（含重连）建立
//   'close'   连接关闭
//   'error'   服务端返回的错误 / 鉴权失败
import store from '@/store'

// WebSocket 服务路径（与 vue.config.js / Nginx 配置保持一致）
const WS_PATH = '/ws'

// 心跳间隔 / 判死超时（毫秒）
const HEARTBEAT_INTERVAL = 25000
const IDLE_TIMEOUT = 60000

// 重连退避时间（毫秒）
const RECONNECT_DELAYS = [1000, 2000, 4000, 6000, 10000]

class ChatSocket {
  constructor () {
    this.ws = null
    this.role = ''
    this.listeners = {}
    this.queue = []
    this.reconnectTimer = null
    this.heartbeatTimer = null
    this.attempts = 0
    this.manualClose = false
    this.lastActiveAt = 0
    this.connected = false
    // 连接状态：'idle' | 'connecting' | 'open' | 'closed' | 'authfail'
    this.state = 'idle'
  }

  // 当前是否可直接发送数据
  get isOpen () {
    return !!(this.connected && this.ws && this.ws.readyState === 1)
  }

  // ==================== 事件 ====================
  on (event, handler) {
    if (!this.listeners[event]) this.listeners[event] = []
    this.listeners[event].push(handler)
  }

  off (event, handler) {
    const list = this.listeners[event]
    if (!list) return
    const index = list.indexOf(handler)
    if (index > -1) list.splice(index, 1)
  }

  emit (event, payload) {
    const list = this.listeners[event]
    if (!list || !list.length) return
    list.slice().forEach(handler => {
      try {
        handler(payload)
      } catch (e) {
        // 单个监听异常不影响其它监听
        console.error('[ws] 事件处理异常:', event, e)
      }
    })
  }

  // ==================== 连接 ====================
  buildUrl () {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return protocol + '//' + window.location.host + WS_PATH
  }

  connect (role = 'user') {
    if (this.isOpen || this.state === 'connecting') return
    if (!store.getters.token) return

    this.role = role
    this.manualClose = false
    this.state = 'connecting'

    let ws
    try {
      ws = new WebSocket(this.buildUrl())
    } catch (e) {
      this.scheduleReconnect()
      return
    }
    this.ws = ws

    ws.onopen = () => {
      this.connected = true
      this.attempts = 0
      this.state = 'open'
      this.lastActiveAt = Date.now()
      // 首帧鉴权（token 取当前最新值，避免使用过期令牌）
      this.raw({ type: 'auth', role: this.role, token: store.getters.token })
      this.startHeartbeat()
      this.flushQueue()
      this.emit('open')
    }

    ws.onmessage = (event) => {
      this.lastActiveAt = Date.now()
      let data
      try {
        data = JSON.parse(event.data)
      } catch (e) {
        return
      }
      this.handleServerMessage(data)
    }

    ws.onerror = () => {
      // onerror 之后必定触发 onclose，这里不重复处理
    }

    ws.onclose = () => {
      this.connected = false
      this.stopHeartbeat()
      this.ws = null
      if (this.state !== 'authfail') {
        this.state = 'closed'
        this.emit('close')
        this.scheduleReconnect()
      }
    }
  }

  handleServerMessage (data) {
    switch (data.type) {
      case 'ready':
        this.emit('ready', data)
        break
      case 'message':
        this.emit('message', data)
        break
      case 'session':
        this.emit('session', data)
        break
      case 'ack':
        this.emit('ack', data)
        break
      case 'read':
        this.emit('read', data)
        break
      case 'revoke':
        this.emit('revoke', data)
        break
      case 'revokeAck':
        this.emit('revokeAck', data)
        break
      case 'pong':
        break
      case 'error':
        if (data.authFail) {
          // 登录失效：停止重连，交由 HTTP 层处理登录态
          this.state = 'authfail'
          this.manualClose = true
        }
        this.emit('error', data)
        break
      default:
        break
    }
  }

  disconnect () {
    this.manualClose = true
    this.state = 'idle'
    this.stopHeartbeat()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      try {
        this.ws.close()
      } catch (e) { /* 忽略 */ }
      this.ws = null
    }
    this.connected = false
    this.queue = []
  }

  scheduleReconnect () {
    if (this.manualClose || this.reconnectTimer) return
    if (!store.getters.token) return
    const delay = RECONNECT_DELAYS[Math.min(this.attempts, RECONNECT_DELAYS.length - 1)]
    this.attempts++
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.state = 'idle'
      this.connect(this.role || 'user')
    }, delay)
  }

  // ==================== 心跳 ====================
  startHeartbeat () {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (!this.isOpen) return
      // 长时间未收到任何数据：判定连接已死，主动关闭触发重连
      if (Date.now() - this.lastActiveAt > IDLE_TIMEOUT) {
        try {
          this.ws.close()
        } catch (e) { /* 忽略 */ }
        return
      }
      this.raw({ type: 'ping' })
    }, HEARTBEAT_INTERVAL)
  }

  stopHeartbeat () {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  // ==================== 发送 ====================
  /** 底层发送（未连接时返回 false） */
  raw (payload) {
    if (!this.isOpen) return false
    try {
      this.ws.send(JSON.stringify(payload))
      return true
    } catch (e) {
      return false
    }
  }

  /** 对外发送：未连接时进入待发队列，连接建立后自动补发 */
  send (payload) {
    if (this.raw(payload)) return true
    this.queue.push(payload)
    this.connect(this.role || 'user')
    return false
  }

  flushQueue () {
    if (!this.queue.length) return
    const list = this.queue.slice()
    this.queue = []
    list.forEach(payload => this.raw(payload))
  }

  /** 发送聊天消息（返回 false 表示 WebSocket 不可用，请走 HTTP 兜底） */
  sendChatMessage (sessionId, content, msgType = 1, clientMsgId = '') {
    return this.send({
      type: 'send',
      sessionId,
      content,
      msgType,
      clientMsgId: clientMsgId || ('c' + Date.now() + Math.random().toString(16).slice(2))
    })
  }

  /** 撤回消息（返回 false 表示 WebSocket 不可用，请走 HTTP 兜底） */
  sendRevokeMessage (sessionId, messageId, clientMsgId = '') {
    return this.send({
      type: 'revoke',
      sessionId,
      messageId,
      clientMsgId: clientMsgId || ('r' + Date.now() + Math.random().toString(16).slice(2))
    })
  }

  /** 上报已读（买家推进 user_last_read_id，客服推进 agent_last_read_id） */
  sendRead (sessionId, lastMsgId = 0) {
    return this.send({ type: 'read', sessionId, lastMsgId })
  }
}

// 全局单例：整个商城端共用一个 WebSocket 连接
const chatSocket = new ChatSocket()

export default chatSocket
export { ChatSocket }

